import Image from 'next/image';

import { Typography, Box, Grid, Divider } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { IDefaultGlances } from 'src/interfaces/product';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';

type TextIcon = {
  icon?: JSX.Element;
  title?: string;
  subTitle?: string;
};

type Props = {
  defaultGlances: IDefaultGlances;
};

const ProductDetailDefaultGlance = ({ defaultGlances }: Props) => {
  // common variable
  const { t } = useTranslation('common');

  const glancesBundleItems = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-book" />,
    title: t('product_detail.default_glance.bundle_items.title', {
      courseAmount: defaultGlances.total_items,
    }),
    subTitle: `${t('product_detail.default_glance.bundle_items.subtitle')}`,
  });

  const glancesVideo = () => {
    const hours = Math.floor((defaultGlances.total_hours || 0) / 3600);
    const minute = Math.floor(((defaultGlances.total_hours || 0) / 60) % 60);
    if (hours < 1) {
      return {
        icon: <FontAwesomeIcon icon="fa-light fa-circle-play" />,
        title: t('cpd_course.default_glance.all_video', { total: defaultGlances.total_videos }),
        subTitle: t('cpd_course.default_glance.all_video.time.only_minute', {
          minute,
        }),
      };
    }

    return {
      icon: <FontAwesomeIcon icon="fa-light fa-circle-play" />,
      title: t('cpd_course.default_glance.all_video', { total: defaultGlances.total_videos }),
      subTitle: t('cpd_course.default_glance.all_video.time', {
        hours,
        minute,
      }),
    };
  };

  const glancesAvailableDaysToLearn = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-hourglass-start" />,
    title: t('product_detail.default_glance.available_days_to_learn'),
    subTitle: `${t('cpd_course.default_glance.available_days_to_learn.description')}\n${t(
      'cpd_course.default_glance.available_days_to_learn.remark',
    )}`,
  });

  const glancesBundleCertificate = () => ({
    icon: <Image layout="fixed" src="/static/icons/glance-certificate.svg" width="33px" height="38px" alt="cert" />,
    title: t('product_detail.default_glance.bundle.certificates.title', {
      cert_amount: defaultGlances.total_certificates,
    }),
    subTitle: t('product_detail.default_glance.bundle.certificates.subtitle'),
  });

  const glancesFileDownload = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-file-arrow-down" />,
    title: t('cpd_course.default_glance.file_download'),
    subTitle: t('cpd_course.default_glance.file_download.description', {
      total: defaultGlances.total_downloaded_files,
    }),
  });

  const glancesExam = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-circle-question" />,
    title: t('cpd_course.default_glance.exam'),
    subTitle: t('cpd_course.default_glance.exam.description', { total: defaultGlances.total_quizzes }),
  });

  const glancesArticle = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-file-lines" />,
    title: t('cpd_course.default_glance.article'),
    subTitle: t('cpd_course.default_glance.article.description', {
      total: defaultGlances.total_articles,
    }),
  });

  const renderDefaultGlance = () => {
    if (!defaultGlances) return;

    const list: TextIcon[] = [];

    if (defaultGlances.total_items) list.push(glancesBundleItems());
    if (defaultGlances.total_hours) list.push(glancesVideo());
    list.push(glancesAvailableDaysToLearn());
    if (defaultGlances.total_certificates) list.push(glancesBundleCertificate());
    if (defaultGlances.total_downloaded_files) list.push(glancesFileDownload());
    if (defaultGlances.total_quizzes) list.push(glancesExam());
    if (defaultGlances.total_articles) list.push(glancesArticle());

    return (
      <Grid container spacing={2} sx={{}}>
        {list.map(({ icon, title = '', subTitle = '' }: TextIcon, index) => (
          <Grid item xs={12} md={6} lg={12} key={index}>
            <Box className="default-glance">
              <Box sx={{ ml: '8px', display: 'flex', textAlign: 'center', justifyContent: 'center' }}>{icon}</Box>
              <Box sx={{ marginLeft: '32px' }}>
                <Typography variant="h4" sx={{ lineHeight: '22px', whiteSpace: 'pre-wrap' }}>
                  {title}
                </Typography>
                <div className="subtitle">{subTitle}</div>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <div>
      <div className="course-description-title-mobile">
        <Typography color="black" variant="h4" fontWeight="500">
          {t('cpd_course.course_description.title')}
        </Typography>
        <Box mt={1} mb={4}>
          <Divider />
        </Box>
      </div>
      <div>{renderDefaultGlance()}</div>
    </div>
  );
};

export default ProductDetailDefaultGlance;
